import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './EditOpinionFieldsPage.css';

const API_BASE_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  return url ? url + '/api' : '/api';
})();

// =============================================================================
// FIELD GROUPING CONFIG (Frontend handles grouping, not backend)
// =============================================================================
const FIELD_GROUPS = {
  document_info: {
    label: "Document Info",
    icon: "📌",
    fields: ["document_type", "document_number", "document_date", "sro", "original_status"]
  },
  parties: {
    label: "Parties",
    icon: "👤",
    fields: ["vendor", "vendee", "executed_by", "in_favour_of"]
  },
  property_details: {
    label: "Property Details",
    icon: "🧭",
    fields: ["survey_number", "village", "mandal", "district"]
  },
  extent: {
    label: "Extent",
    icon: "📏",
    fields: ["extent_sqyds", "extent_sqmtrs"]
  },
  boundaries: {
    label: "Boundaries",
    icon: "🗺️",
    fields: ["boundaries_north", "boundaries_south", "boundaries_east", "boundaries_west"]
  },
  consideration: {
    label: "Consideration",
    icon: "💰",
    fields: ["consideration", "loan_amount"]
  }
};

// Critical fields that must be fixed (mandatory across ALL property docs
// — Feb 2026 user directive). ``survey_number`` and extent (either
// Sq.Yds OR Sq.Mtrs — satisfied by the "extent either-or" rule below)
// must be filled before the opinion can regenerate.
const CRITICAL_FIELDS = [
  "document_date", "document_number", "vendor", "vendee",
  "executed_by", "in_favour_of", "survey_number", "loan_amount",
  "ec_from_date", "ec_to_date",
  "extent_sqyds", "extent_sqmtrs",
];

// Helper: extent is considered SATISFIED when EITHER extent_sqyds or
// extent_sqmtrs carries a non-empty value on the same doc. Used by the
// local risk calculator so the UI doesn't redden BOTH twins when one
// is empty — matches the backend rule in extraction_validator.py.
const isExtentSatisfied = (docFields) => {
  const sy = (docFields?.extent_sqyds?.value || '').toString().trim();
  const sm = (docFields?.extent_sqmtrs?.value || '').toString().trim();
  return sy.length > 0 || sm.length > 0;
};

// Field display names
const FIELD_LABELS = {
  document_type: "Document Type",
  document_number: "Document Number",
  document_date: "Document Date",
  sro: "Sub-Registrar Office",
  original_status: "Original/Xerox",
  vendor: "Vendor (Seller)",
  vendee: "Vendee (Buyer)",
  executed_by: "Executed By",
  in_favour_of: "In Favour Of",
  survey_number: "Survey Number",
  village: "Village",
  mandal: "Mandal",
  district: "District",
  extent_sqyds: "Extent (Sq. Yards)",
  extent_sqmtrs: "Extent (Sq. Meters)",
  boundaries_north: "North Boundary",
  boundaries_south: "South Boundary",
  boundaries_east: "East Boundary",
  boundaries_west: "West Boundary",
  consideration: "Consideration Amount",
  loan_amount: "Loan Amount"
};

// =============================================================================
// RISK INDICATOR COMPONENT
// =============================================================================
const RiskBadge = ({ risk }) => {
  const badges = {
    green: { icon: "✅", className: "risk-green", label: "Valid" },
    yellow: { icon: "⚠️", className: "risk-yellow", label: "Review" },
    red: { icon: "🔴", className: "risk-red", label: "Must Fix" }
  };
  const badge = badges[risk] || badges.green;
  return <span className={`risk-badge ${badge.className}`} title={badge.label}>{badge.icon}</span>;
};

// =============================================================================
// EDITABLE FIELD COMPONENT
// =============================================================================
const EditableField = ({ fieldName, fieldData, onChange, isCritical }) => {
  const { value, risk } = fieldData;
  const label = FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  // Extent twin hint — when one of the two extent fields is critical,
  // nudge the reviewer that filling either Sq.Yds OR Sq.Mtrs satisfies
  // the mandate (matches the "extent either-or" backend rule).
  const isExtentTwin = fieldName === 'extent_sqyds' || fieldName === 'extent_sqmtrs';
  const extentHint = isExtentTwin
    ? 'Fill either Sq. Yards or Sq. Meters — one is required.'
    : null;

  return (
    <div
      className={`editable-field ${risk === 'red' ? 'field-error' : risk === 'yellow' ? 'field-warning' : 'field-valid'}`}
      data-testid={`correction-field-${fieldName}`}
    >
      <label>
        {label}
        {isCritical && <span className="critical-marker">*</span>}
        <RiskBadge risk={risk} />
      </label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        className={`field-input risk-${risk}`}
        placeholder={`Enter ${label.toLowerCase()}`}
        data-testid={`correction-input-${fieldName}`}
      />
      {extentHint && (
        <small
          className="field-hint"
          data-testid={`correction-hint-${fieldName}`}
          style={{ display: 'block', marginTop: 4, color: '#6b7280' }}
        >
          {extentHint}
        </small>
      )}
    </div>
  );
};

// =============================================================================
// FIELD GROUP COMPONENT
// =============================================================================
const FieldGroup = ({ groupKey, groupConfig, fields, onChange }) => {
  const { label, icon, fields: fieldKeys } = groupConfig;
  
  // Filter to only show fields that exist in the document
  const availableFields = fieldKeys.filter(key => fields[key]);
  
  if (availableFields.length === 0) return null;
  
  return (
    <div className="field-group">
      <h3 className="field-group-title">{icon} {label}</h3>
      <div className="field-group-content">
        {availableFields.map(fieldKey => (
          <EditableField
            key={fieldKey}
            fieldName={fieldKey}
            fieldData={fields[fieldKey]}
            onChange={onChange}
            isCritical={CRITICAL_FIELDS.includes(fieldKey)}
          />
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// DOCUMENT TAB COMPONENT
// =============================================================================
const DocumentTab = ({ doc, index, isActive, onClick }) => {
  const statusIcons = {
    green: "✅",
    yellow: "⚠️",
    red: "🔴"
  };
  
  return (
    <button
      className={`doc-tab ${isActive ? 'active' : ''} status-${doc.doc_status}`}
      onClick={() => onClick(index)}
    >
      <span className="doc-tab-status">{statusIcons[doc.doc_status]}</span>
      <span className="doc-tab-type">{doc.document_type}</span>
      <span className="doc-tab-index">#{index + 1}</span>
    </button>
  );
};

// =============================================================================
// SUMMARY BAR COMPONENT
// =============================================================================
const SummaryBar = ({ summary, onJumpToErrors }) => {
  const { total_docs, docs_with_issues, critical_errors, can_generate } = summary;
  
  return (
    <div className={`summary-bar ${can_generate ? 'can-generate' : 'has-errors'}`}>
      <div className="summary-stats">
        <span className="stat">
          <span className="stat-value">{total_docs}</span>
          <span className="stat-label">Documents</span>
        </span>
        <span className="stat-divider">|</span>
        <span className={`stat ${docs_with_issues > 0 ? 'warning' : ''}`}>
          <span className="stat-value">{docs_with_issues}</span>
          <span className="stat-label">With Issues</span>
        </span>
        <span className="stat-divider">|</span>
        <span className={`stat ${critical_errors > 0 ? 'error' : ''}`}>
          <span className="stat-value">{critical_errors}</span>
          <span className="stat-label">Critical Errors</span>
        </span>
      </div>
      {critical_errors > 0 && (
        <button className="jump-errors-btn" onClick={onJumpToErrors}>
          Jump to Errors
        </button>
      )}
    </div>
  );
};

// =============================================================================
// PROGRESS BAR COMPONENT
// =============================================================================
const ProgressBar = ({ documents, activeIndex, onSelect }) => {
  return (
    <div className="progress-bar">
      {documents.map((doc, idx) => (
        <button
          key={idx}
          className={`progress-dot status-${doc.doc_status} ${idx === activeIndex ? 'active' : ''}`}
          onClick={() => onSelect(idx)}
          title={`${doc.document_type} - ${doc.doc_status.toUpperCase()}`}
        >
          {idx + 1}
        </button>
      ))}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT - ExtractionReviewPage
// Uses extraction_id to fetch document-wise data BEFORE generation
// =============================================================================
const ExtractionReviewPage = () => {
  const { extractionId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Document-wise state
  const [summary, setSummary] = useState({ total_docs: 0, docs_with_issues: 0, critical_errors: 0, can_generate: true });
  const [documents, setDocuments] = useState([]);
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [metadata, setMetadata] = useState({});

  useEffect(() => {
    loadReviewData();
  }, [extractionId]);

  const loadReviewData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/extraction/${extractionId}/review-documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSummary(response.data.summary || { total_docs: 0, docs_with_issues: 0, critical_errors: 0, can_generate: true });
        setDocuments(response.data.documents || []);
        setMetadata(response.data.metadata || {});
        
        // Set active doc to first one with errors
        const firstErrorIdx = (response.data.documents || []).findIndex(d => d.doc_status === 'red');
        if (firstErrorIdx >= 0) {
          setActiveDocIndex(firstErrorIdx);
        }
      }
    } catch (err) {
      console.error('Error loading review data:', err);
      setError(err.response?.data?.detail || 'Failed to load extraction data');
    } finally {
      setLoading(false);
    }
  };

  // Handle field change for a specific document
  const handleFieldChange = (fieldName, value) => {
    setDocuments(prevDocs => {
      const newDocs = [...prevDocs];
      const doc = { ...newDocs[activeDocIndex] };
      const fields = { ...doc.fields };

      // Update value for this field first so the either-or check below
      // sees the new value.
      fields[fieldName] = {
        ...fields[fieldName],
        value: value,
        risk: calculateLocalRisk(fieldName, value, doc.document_type, fields),
      };

      // When this field is an extent twin, re-evaluate the OTHER twin
      // so its red/green state matches the either-or rule.
      if (fieldName === 'extent_sqyds' && fields.extent_sqmtrs) {
        fields.extent_sqmtrs = {
          ...fields.extent_sqmtrs,
          risk: calculateLocalRisk(
            'extent_sqmtrs',
            fields.extent_sqmtrs.value,
            doc.document_type,
            fields,
          ),
        };
      } else if (fieldName === 'extent_sqmtrs' && fields.extent_sqyds) {
        fields.extent_sqyds = {
          ...fields.extent_sqyds,
          risk: calculateLocalRisk(
            'extent_sqyds',
            fields.extent_sqyds.value,
            doc.document_type,
            fields,
          ),
        };
      }

      doc.fields = fields;

      // Recalculate doc status
      const hasRed = Object.values(fields).some(f => f.risk === 'red');
      const hasYellow = Object.values(fields).some(f => f.risk === 'yellow');
      doc.doc_status = hasRed ? 'red' : (hasYellow ? 'yellow' : 'green');

      newDocs[activeDocIndex] = doc;
      return newDocs;
    });

    // Update summary
    updateSummary();
  };
  
  // Local risk calculation (matches backend logic)
  const calculateLocalRisk = (fieldName, value, docType, docFields = null) => {
    const strValue = (value || '').trim();
    const isCritical = CRITICAL_FIELDS.includes(fieldName);

    // Extent either-or rule: if this empty extent twin has its
    // counterpart filled on the same doc, treat as green (satisfied).
    if (
      isCritical
      && !strValue
      && (fieldName === 'extent_sqyds' || fieldName === 'extent_sqmtrs')
      && docFields
      && isExtentSatisfied(docFields)
    ) {
      return 'green';
    }

    // Empty critical = red
    if (!strValue && isCritical) return 'red';
    // Empty non-critical = yellow
    if (!strValue) return 'yellow';
    // Contains ? = yellow
    if (strValue.includes('?')) return 'yellow';
    // Valid date check
    if (['document_date', 'ec_from_date', 'ec_to_date'].includes(fieldName)) {
      const datePattern = /^\d{1,2}[-/.]\d{1,2}[-/.]\d{4}$/;
      if (!datePattern.test(strValue)) {
        return isCritical ? 'red' : 'yellow';
      }
    }
    return 'green';
  };
  
  // Update summary after changes
  const updateSummary = () => {
    setSummary(prev => {
      let criticalErrors = 0;
      let docsWithIssues = 0;
      
      documents.forEach(doc => {
        if (doc.doc_status === 'red' || doc.doc_status === 'yellow') {
          docsWithIssues++;
        }
        Object.entries(doc.fields).forEach(([key, field]) => {
          if (CRITICAL_FIELDS.includes(key) && field.risk === 'red') {
            criticalErrors++;
          }
        });
      });
      
      return {
        ...prev,
        docs_with_issues: docsWithIssues,
        critical_errors: criticalErrors,
        can_generate: criticalErrors === 0
      };
    });
  };

  // Jump to first document with errors
  const handleJumpToErrors = () => {
    const firstErrorIdx = documents.findIndex(d => d.doc_status === 'red');
    if (firstErrorIdx >= 0) {
      setActiveDocIndex(firstErrorIdx);
    }
  };

  // Navigation
  const handlePrevDoc = () => {
    if (activeDocIndex > 0) {
      setActiveDocIndex(activeDocIndex - 1);
    }
  };

  const handleNextDoc = () => {
    if (activeDocIndex < documents.length - 1) {
      setActiveDocIndex(activeDocIndex + 1);
    }
  };

  // Save corrections to backend
  const handleSaveCorrections = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Save each document's corrections
      for (const [docIdx, doc] of documents.entries()) {
        for (const [fieldName, fieldData] of Object.entries(doc.fields)) {
          await axios.put(
            `${API_BASE_URL}/extraction/${extractionId}/update-field`,
            {
              doc_index: docIdx,
              field_name: fieldName,
              value: fieldData.value,
              document_type: doc.document_type
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }
      
      setSuccessMessage('Corrections saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving corrections:', err);
      alert('Failed to save corrections');
    } finally {
      setSaving(false);
    }
  };

  // Continue to format selection (go back to ExtractPage)
  const handleContinueToFormat = () => {
    // Navigate back to extract page to select format and generate
    navigate(`/extract/${extractionId}`);
  };

  // Current active document
  const activeDoc = documents[activeDocIndex] || null;

  // Memoized check for blocking errors
  const hasBlockingErrors = useMemo(() => {
    return documents.some(doc =>
      Object.entries(doc.fields || {}).some(([key, field]) =>
        CRITICAL_FIELDS.includes(key) && field.risk === 'red'
      )
    );
  }, [documents]);

  if (loading) {
    return (
      <div className="edit-fields-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading extracted documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-fields-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="edit-fields-page">
        <div className="edit-header">
          <div className="header-left">
            <button onClick={() => navigate(`/extract/${extractionId}`)} className="btn-back">← Back to Format Selection</button>
            <h1>Review Extracted Documents</h1>
          </div>
        </div>
        <div className="no-docs-message">
          <p>No documents found for review. The extraction may not have produced document-level data.</p>
          <button onClick={() => navigate(`/extract/${extractionId}`)} className="btn-back">Continue to Format Selection</button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-fields-page">
      {/* Header */}
      <div className="edit-header">
        <div className="header-left">
          <button onClick={() => navigate(`/extract/${extractionId}`)} className="btn-back">← Back</button>
          <h1>Review Extracted Documents</h1>
          <span className="format-badge">HUMAN CORRECTION GATE</span>
        </div>
        <div className="header-right">
          <button onClick={handleSaveCorrections} disabled={saving} className="btn-save">
            {saving ? 'Saving...' : '💾 Save Corrections'}
          </button>
          <button 
            onClick={handleContinueToFormat} 
            disabled={hasBlockingErrors} 
            className={`btn-export ${hasBlockingErrors ? 'btn-disabled' : ''}`}
            title={hasBlockingErrors ? 'Fix critical errors first' : 'Continue to format selection'}
          >
            {hasBlockingErrors ? '🔴 Fix Errors First' : '✅ Continue to Generate'}
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <SummaryBar summary={summary} onJumpToErrors={handleJumpToErrors} />

      {/* Success/Error Messages */}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {hasBlockingErrors && (
        <div className="blocking-error-banner">
          <span className="error-icon">🚨</span>
          <span>Please fix {summary.critical_errors} critical error(s) before generating the opinion.</span>
          <button onClick={handleJumpToErrors}>Jump to Errors</button>
        </div>
      )}

      {/* Progress Bar */}
      <ProgressBar 
        documents={documents} 
        activeIndex={activeDocIndex} 
        onSelect={setActiveDocIndex} 
      />

      {/* Document Tabs */}
      <div className="doc-tabs-container">
        <div className="doc-tabs">
          {documents.map((doc, idx) => (
            <DocumentTab
              key={idx}
              doc={doc}
              index={idx}
              isActive={idx === activeDocIndex}
              onClick={setActiveDocIndex}
            />
          ))}
        </div>
      </div>

      {/* Active Document Content */}
      {activeDoc && (
        <div className="document-editor">
          <div className="document-header">
            <h2>
              <span className={`doc-status-icon status-${activeDoc.doc_status}`}>
                {activeDoc.doc_status === 'green' ? '✅' : activeDoc.doc_status === 'yellow' ? '⚠️' : '🔴'}
              </span>
              {activeDoc.document_type}
              <span className="doc-index">Document {activeDocIndex + 1} of {documents.length}</span>
            </h2>
          </div>

          <div className="field-groups-container">
            {Object.entries(FIELD_GROUPS).map(([groupKey, groupConfig]) => (
              <FieldGroup
                key={groupKey}
                groupKey={groupKey}
                groupConfig={groupConfig}
                fields={activeDoc.fields}
                onChange={handleFieldChange}
              />
            ))}
            
            {/* Render any remaining fields not in groups */}
            {(() => {
              const groupedFields = new Set(
                Object.values(FIELD_GROUPS).flatMap(g => g.fields)
              );
              const ungroupedFields = Object.entries(activeDoc.fields)
                .filter(([key]) => !groupedFields.has(key) && !key.startsWith('_'));
              
              if (ungroupedFields.length === 0) return null;
              
              return (
                <div className="field-group">
                  <h3 className="field-group-title">📋 Other Fields</h3>
                  <div className="field-group-content">
                    {ungroupedFields.map(([fieldKey, fieldData]) => (
                      <EditableField
                        key={fieldKey}
                        fieldName={fieldKey}
                        fieldData={fieldData}
                        onChange={handleFieldChange}
                        isCritical={CRITICAL_FIELDS.includes(fieldKey)}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Document Navigation */}
          <div className="doc-navigation">
            <button 
              onClick={handlePrevDoc} 
              disabled={activeDocIndex === 0}
              className="nav-btn prev"
            >
              ◀ Previous Document
            </button>
            <span className="nav-info">
              Document {activeDocIndex + 1} of {documents.length}
            </span>
            <button 
              onClick={handleNextDoc} 
              disabled={activeDocIndex === documents.length - 1}
              className="nav-btn next"
            >
              Next Document ▶
            </button>
          </div>
        </div>
      )}

      {/* Final Action Buttons */}
      <div className="form-actions">
        <button onClick={handleSaveCorrections} disabled={saving} className="btn-save-large">
          {saving ? 'Saving...' : '💾 Save All Corrections'}
        </button>
        <button 
          onClick={handleContinueToFormat} 
          disabled={hasBlockingErrors} 
          className={`btn-export-large ${hasBlockingErrors ? 'btn-disabled' : ''}`}
        >
          {hasBlockingErrors ? '🔴 Fix Critical Errors First' : '✅ Continue to Generate Opinion'}
        </button>
      </div>
    </div>
  );
};

export default ExtractionReviewPage;
